import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFOrdersEntities,
} from '@pf/shared-common';
import { createMap, forMember, mapFrom, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withFragmentSearchFilter,
    withMetadata,
    withSearchFilter,
    withSearchFilterUnTyped,
} from '@pf/shared-utility';
import {
    PurchaseOrderHeaderCreate,
    PurchaseOrderHeaderDto,
    PurchaseOrderHeaderRead,
    PurchaseOrderHeaderSearchParams,
} from '../../entities/purchase-order-header.dto';
import {
    CurrencySymbol,
    CustomFieldMetadataMap,
    ResourceAuthSymbol,
} from '@pf/shared/util-platform';

const symbols = entitySymbols(PFOrdersEntities.PurchaseOrderHeader);

withDefaultEntityMetadata<PurchaseOrderHeaderDto>(symbols.dto, {
    billToLocation: Object,
    customFields: [CustomFieldMetadataMap.symbols.dto],
    dateRequiredBy: String,
    externalReferenceId: String,
    notes: String,
    orderDate: String,
    orderNumber: String,
    orderTotalPrice: CurrencySymbol,
    purchaseOrderApprovedBy: String,
    purchaseOrderApprovedDate: String,
    purchaseOrderLines: [Object],
    requisitionId: String,
    resourceAuth: ResourceAuthSymbol,
    salesQuoteDate: String,
    salesQuoteNumber: String,
    shipToLocation: Object,
    status: String,
    taxes: CurrencySymbol,
    termsAndConditions: String,
    totalPrice: CurrencySymbol,
    vendor: Object,
});

withDefaultEntityMetadata<PurchaseOrderHeaderRead>(symbols.entity, {
    billToLocation: Object,
    customFields: [CustomFieldMetadataMap.symbols.entity],
    dateRequiredBy: String,
    externalReferenceId: String,
    notes: String,
    orderDate: String,
    orderNumber: String,
    orderTotalPrice: CurrencySymbol,
    purchaseOrderApprovedBy: String,
    purchaseOrderApprovedDate: String,
    purchaseOrderLines: [Object],
    requisitionId: String,
    resourceAuth: ResourceAuthSymbol,
    salesQuoteDate: String,
    salesQuoteNumber: String,
    shipToLocation: Object,
    status: String,
    taxes: CurrencySymbol,
    termsAndConditions: String,
    totalPrice: CurrencySymbol,
    vendor: Object,
});

withMetadata<PurchaseOrderHeaderCreate>(symbols.createBody, {
    billToLocationId: String,
    dateRequiredBy: String,
    externalReferenceId: String,
    notes: String,
    orderDate: String,
    orderNumber: String,
    orderTotalPrice: CurrencySymbol,
    purchaseOrderApprovedBy: String,
    purchaseOrderApprovedDate: String,
    requisitionId: String,
    resourceAuth: ResourceAuthSymbol,
    salesQuoteDate: String,
    salesQuoteNumber: String,
    shipToLocationId: String,
    status: String,
    taxes: CurrencySymbol,
    termsAndConditions: String,
    totalPrice: CurrencySymbol,
    vendorId: String,
});

withDefaultSearchParamsMetadata<PurchaseOrderHeaderSearchParams>(
    symbols.searchParams,
    {
        externalReferenceId: String,
        orderNumber: String,
        shipToLocationId: String,
        billToLocationId: String,
        orderDateStart: String,
        orderDateEnd: String,
    }
);

export const PurchaseOrderHeaderMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<PurchaseOrderHeaderRead, PurchaseOrderHeaderDto>(
            mapper,
            symbols.entity,
            symbols.dto
        );
        createMap<PurchaseOrderHeaderDto, PurchaseOrderHeaderRead>(
            mapper,
            symbols.dto,
            symbols.entity
        );
        createMap<PurchaseOrderHeaderDto, PurchaseOrderHeaderCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            forMember(
                x => x.billToLocationId,
                mapFrom(dto => dto.billToLocation?.id)
            ),
            forMember(
                x => x.shipToLocationId,
                mapFrom(dto => dto.shipToLocation?.id)
            )
        );
        createMap<
            IQueryParams<PurchaseOrderHeaderDto>,
            PurchaseOrderHeaderSearchParams
        >(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            withSearchFilter('externalReferenceId'),
            withSearchFilter('orderNumber'),
            withFragmentSearchFilter('shipToLocation'),
            withFragmentSearchFilter('billToLocation'),
            withSearchFilterUnTyped('orderDateStart'),
            withSearchFilterUnTyped('orderDateEnd'),
            ...withDefaultSearchParamsMappingConfiguration()
        );
    },
};
